'use client';

import {useMemo} from 'react';
import {cn} from '@tinct/core-utils';
import {Markdown} from '@tinct/web-components/markdown';

// ============================================================================
// Types
// ============================================================================

interface TextContentProps {
  text: string;
  isStreaming?: boolean;
  className?: string;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Regex that matches tinct-instruction tags (with any attributes and content).
 */
const INSTRUCTION_TAG_PATTERN =
  /<tinct-instruction\s+[^>]*>[\s\S]*?<\/tinct-instruction>/g;

/**
 * Strip all tinct-instruction tags from text before markdown rendering.
 * More reliable than relying on the markdown parser to recognize
 * custom elements with non-standard attribute syntax.
 */
function stripInstructionTags(text: string): string {
  if (!text.includes('<tinct-instruction')) return text;
  return text.replace(INSTRUCTION_TAG_PATTERN, '');
}

// ============================================================================
// Component
// ============================================================================

/**
 * Renders markdown text content using Streamdown.
 *
 * Strips any `<tinct-instruction>` tags before rendering.
 * Instruction extraction is handled separately by the useInstructions hook.
 */
export function TextContent({
  text,
  isStreaming = false,
  className,
}: TextContentProps) {
  const cleanText = useMemo(() => stripInstructionTags(text), [text]);

  return (
    <div className={cn(className)}>
      <Markdown parseIncompleteMarkdown={true}>
        {cleanText}
      </Markdown>
    </div>
  );
}
